import React, { useEffect, useMemo, useState } from 'react';
import { X, MapPin } from 'lucide-react';
import { Button, Input, TextArea } from '../shared';
import CustomSelect from '../shared/CustomSelect';
import { useAdminCountries } from '../../services/countryService';

const TAX_MODULES = [
  { value: 'none', label: 'None' },
  { value: 'canada_gst_hst', label: 'Canada GST/HST' },
  { value: 'us_sales_tax', label: 'US sales tax' },
  { value: 'eu_vat', label: 'EU VAT' },
  { value: 'ghana_vat', label: 'Ghana VAT' },
  { value: 'nigeria_vat', label: 'Nigeria VAT' },
  { value: 'south_africa_vat', label: 'South Africa VAT' },
  { value: 'manual', label: 'Manual' },
];

const TAX_ENGINES = [
  { value: 'none', label: 'None (Phase 1)' },
  { value: 'stripe_tax', label: 'Stripe Tax' },
  { value: 'taxjar', label: 'TaxJar' },
  { value: 'avalara', label: 'Avalara' },
  { value: 'manual', label: 'Manual rates' },
];

const PAYMENT_RAILS = [
  { value: 'stripe_platform', label: 'Stripe platform account' },
  { value: 'stripe_connect', label: 'Stripe Connect' },
  { value: 'local_psp', label: 'Local PSP' },
];

const ROLES = [
  { value: 'facilitator', label: 'Facilitator' },
  { value: 'agent', label: 'Agent' },
  { value: 'merchant', label: 'Merchant' },
];

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

const PAYOUT_POLICIES = [
  { value: 'after_event_completed', label: 'After event completed' },
  { value: 'after_hold_days', label: 'After hold days' },
  { value: 'manual_only', label: 'Manual only' },
];

const CUSTOMER_FIELD_CHIPS = [
  { value: 'event_country', label: 'Event country' },
  { value: 'billing_country', label: 'Billing country' },
  { value: 'customer_type', label: 'Customer type' },
  { value: 'customer_tax_id', label: 'Customer tax ID' },
  { value: 'event_address', label: 'Event address' },
  { value: 'event_location', label: 'Event location' },
];

const VENDOR_FIELD_CHIPS = [
  { value: 'legal_name', label: 'Legal name' },
  { value: 'tax_residence_country', label: 'Tax residence' },
  { value: 'tax_id', label: 'Tax ID' },
  { value: 'gst_hst_registered', label: 'GST/HST registered' },
  { value: 'gst_hst_number', label: 'GST/HST number' },
  { value: 'vat_registered', label: 'VAT registered' },
  { value: 'w9_status', label: 'W-9 status' },
  { value: 'address_line1', label: 'Address' },
  { value: 'payout_country', label: 'Payout country' },
];

const emptyForm = {
  country_code: '',
  display_name: '',
  status: 'draft',
  default_display_currency: 'USD',
  currencies_allowed: 'USD',
  payment_rails: 'stripe_platform',
  tax_module: 'none',
  tax_engine_provider: 'none',
  marketplace_role: 'facilitator',
  payout_hold_days: 0,
  payout_policy: 'after_event_completed',
  bookings_enabled: true,
  payouts_enabled: true,
  tax_collect_enabled: false,
  tax_rate_percent: '',
  reporting_flags: '',
  checkout_legal_copy: '',
  required_customer_fields: [],
  required_vendor_fields: [],
  sort_order: 0,
};

const FieldChipGroup = ({ label, options, selected, onToggle }) => (
  <div>
    <p className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">{label}</p>
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const on = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
              on
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-primary-600 dark:border-primary-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

const CreateMarketModal = ({
  isOpen,
  onClose,
  onSave,
  market = null,
  isEdit = false,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const { data: countriesData } = useAdminCountries({});
  const countries = countriesData?.data || [];

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        value: c.code,
        label: `${c.name} (${c.code})`,
      })),
    [countries]
  );

  useEffect(() => {
    if (isEdit && market) {
      setFormData({
        country_code: market.country_code || '',
        display_name: market.display_name || '',
        status: market.status || 'draft',
        default_display_currency: market.default_display_currency || 'USD',
        currencies_allowed: Array.isArray(market.currencies_allowed)
          ? market.currencies_allowed.join(', ')
          : market.currencies_allowed || 'USD',
        payment_rails: market.payment_rails || 'stripe_platform',
        tax_module: market.tax_module || 'none',
        tax_engine_provider: market.tax_engine_provider || 'none',
        marketplace_role: market.marketplace_role || 'facilitator',
        payout_hold_days: market.payout_hold_days ?? 0,
        payout_policy: market.payout_policy || 'after_event_completed',
        bookings_enabled: market.bookings_enabled !== false,
        payouts_enabled: market.payouts_enabled !== false,
        tax_collect_enabled: Boolean(market.tax_collect_enabled),
        tax_rate_percent:
          market.tax_rate_percent != null && market.tax_rate_percent !== ''
            ? String(market.tax_rate_percent)
            : '',
        reporting_flags: Array.isArray(market.reporting_flags)
          ? market.reporting_flags.join(', ')
          : '',
        checkout_legal_copy: market.checkout_legal_copy || '',
        required_customer_fields: Array.isArray(market.required_customer_fields)
          ? market.required_customer_fields
          : [],
        required_vendor_fields: Array.isArray(market.required_vendor_fields)
          ? market.required_vendor_fields
          : [],
        sort_order: market.sort_order ?? 0,
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
  }, [isEdit, market, isOpen]);

  if (!isOpen) return null;

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setField(name, type === 'checkbox' ? checked : value);
  };

  const handleCountryPick = (code) => {
    const country = countries.find((c) => c.code === code);
    setFormData((prev) => ({
      ...prev,
      country_code: code,
      display_name: prev.display_name || country?.name || '',
    }));
    if (errors.country_code) setErrors((prev) => ({ ...prev, country_code: '' }));
  };

  const toggleChip = (key, value) => {
    setFormData((prev) => {
      const current = Array.isArray(prev[key]) ? prev[key] : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
  };

  const validate = () => {
    const next = {};
    if (!formData.country_code) next.country_code = 'Country is required';
    if (!formData.display_name.trim()) next.display_name = 'Display name is required';
    if (!formData.default_display_currency || formData.default_display_currency.length !== 3) {
      next.default_display_currency = 'Use a 3-letter currency code';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const currencies = String(formData.currencies_allowed || '')
      .split(/[,\s]+/)
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    const flags = String(formData.reporting_flags || '')
      .split(/[,\s]+/)
      .map((c) => c.trim())
      .filter(Boolean);

    onSave({
      country_code: formData.country_code.toUpperCase(),
      display_name: formData.display_name.trim(),
      status: formData.status,
      default_display_currency: formData.default_display_currency.toUpperCase(),
      currencies_allowed: currencies.length ? currencies : ['USD'],
      payment_rails: formData.payment_rails,
      tax_module: formData.tax_module,
      tax_engine_provider: formData.tax_engine_provider,
      marketplace_role: formData.marketplace_role,
      payout_hold_days: Number(formData.payout_hold_days) || 0,
      payout_policy: formData.payout_policy,
      bookings_enabled: formData.bookings_enabled,
      payouts_enabled: formData.payouts_enabled,
      tax_collect_enabled: formData.tax_collect_enabled,
      tax_rate_percent:
        formData.tax_rate_percent === '' || formData.tax_rate_percent == null
          ? null
          : Number(formData.tax_rate_percent),
      reporting_flags: flags,
      required_customer_fields: formData.required_customer_fields || [],
      required_vendor_fields: formData.required_vendor_fields || [],
      checkout_legal_copy: formData.checkout_legal_copy || null,
      sort_order: Number(formData.sort_order) || 0,
    });
  };

  return (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4 bg-black/40">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
              {isEdit ? 'Edit market' : 'Add market'}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto overflow-x-visible">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CustomSelect
              label="Country from catalog *"
              value={formData.country_code}
              onChange={handleCountryPick}
              options={countryOptions}
              placeholder="Select country"
              disabled={isEdit}
              error={errors.country_code || ''}
              size="sm"
            />
            <Input
              label="Display name *"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              error={errors.display_name || ''}
              size="sm"
            />
            <p className="sm:col-span-2 -mt-2 text-xs text-gray-500 dark:text-gray-400">
              Countries are the reference catalog. Saving this form creates the market the rest of the app uses for operations.
            </p>
            <CustomSelect
              label="Status"
              value={formData.status}
              onChange={(value) => setField('status', value)}
              options={STATUSES}
              size="sm"
            />
            <CustomSelect
              label="Tax module"
              value={formData.tax_module}
              onChange={(value) => setField('tax_module', value)}
              options={TAX_MODULES}
              size="sm"
            />
            <CustomSelect
              label="Tax engine"
              value={formData.tax_engine_provider}
              onChange={(value) => setField('tax_engine_provider', value)}
              options={TAX_ENGINES}
              size="sm"
            />
            <CustomSelect
              label="Payment rails"
              value={formData.payment_rails}
              onChange={(value) => setField('payment_rails', value)}
              options={PAYMENT_RAILS}
              size="sm"
            />
            <CustomSelect
              label="Marketplace role"
              value={formData.marketplace_role}
              onChange={(value) => setField('marketplace_role', value)}
              options={ROLES}
              size="sm"
            />
            <Input
              label="Display currency"
              name="default_display_currency"
              value={formData.default_display_currency}
              onChange={handleInputChange}
              error={errors.default_display_currency || ''}
              maxLength={3}
              size="sm"
            />
            <Input
              label="Currencies allowed (comma-separated)"
              name="currencies_allowed"
              value={formData.currencies_allowed}
              onChange={handleInputChange}
              size="sm"
            />
            <Input
              label="Payout hold days"
              type="number"
              name="payout_hold_days"
              value={formData.payout_hold_days}
              onChange={handleInputChange}
              min={0}
              size="sm"
            />
            <CustomSelect
              label="Payout policy"
              value={formData.payout_policy}
              onChange={(value) => setField('payout_policy', value)}
              options={PAYOUT_POLICIES}
              size="sm"
            />
            <Input
              label="Sort order"
              type="number"
              name="sort_order"
              value={formData.sort_order}
              onChange={handleInputChange}
              min={0}
              size="sm"
            />
          </div>

          <Input
            label="Reporting flags (comma-separated)"
            name="reporting_flags"
            value={formData.reporting_flags}
            onChange={handleInputChange}
            placeholder="cra_platform_report, form_1099k, dac7"
            size="sm"
          />

          <TextArea
            label="Checkout legal copy"
            name="checkout_legal_copy"
            value={formData.checkout_legal_copy}
            onChange={handleInputChange}
            rows={3}
            placeholder="Who the customer contracts with, tax notices, etc."
          />

          <FieldChipGroup
            label="Required customer fields at checkout"
            options={CUSTOMER_FIELD_CHIPS}
            selected={formData.required_customer_fields || []}
            onToggle={(value) => toggleChip('required_customer_fields', value)}
          />

          <FieldChipGroup
            label="Required vendor tax-profile fields"
            options={VENDOR_FIELD_CHIPS}
            selected={formData.required_vendor_fields || []}
            onToggle={(value) => toggleChip('required_vendor_fields', value)}
          />

          <div className="flex flex-wrap gap-4">
            {[
              ['bookings_enabled', 'Bookings enabled'],
              ['payouts_enabled', 'Payouts enabled'],
              ['tax_collect_enabled', 'Collect tax at checkout'],
            ].map(([name, label]) => (
              <label key={name} className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  name={name}
                  checked={Boolean(formData[name])}
                  onChange={handleInputChange}
                  className="rounded border-gray-300"
                />
                {label}
              </label>
            ))}
          </div>

          {formData.tax_collect_enabled ? (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Checkout tax rate (%)
              </label>
              <input
                type="number"
                name="tax_rate_percent"
                min="0"
                max="100"
                step="0.0001"
                value={formData.tax_rate_percent}
                onChange={handleInputChange}
                placeholder="e.g. 15 for Ghana VAT, 8.875 for NY sales tax"
                className="w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 px-3 py-2 text-sm"
              />
              <p className="mt-1 text-[11px] text-gray-500">
                Used at checkout when Stripe Tax is not collecting for a jurisdiction.
              </p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-800">
            <Button type="button" variant="outline" className="!rounded-xl" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" className="!rounded-xl" disabled={isLoading}>
              {isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create market'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMarketModal;
